import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { isBrowser } from "@cinch-labs/shared-util";
import { useUserStore } from "@cinch-nx/data-user";
import { Env, readFromEnv } from "@cinch-nx/environments";
import { initDataDogLogging, initDataDogRUM } from "@cinch-nx/shared-datadog";
import { zendeskMessengerScriptProps } from "@cinch-labs/shared-zendesk-messenger";
import { useRouter } from "next/router";
import Script from "next/script";
import React, { useEffect, useState } from "react";
import { updateVehicleInformation } from "../data-layer";
import { DirectCallRules } from "../direct-call-rules";
import { getStandardPageInfo, updatePageInfo } from "../event-page-view";
import { resetProductDisplay } from "../product-display";
import { segmentSnippet } from "../segment/initialisation-script";
import { SimpleAnalyticsScripts } from "../simple-analytics/script-loader";
import { TrackingEventTypes } from "./types";
import { getCookieConsentPreferences, useAnalytics } from "./use-analytics.hook";
const TIKTOK_CLICKID_QUERY_PARAMETER = "ttclid";
const isPerformanceNavigationTiming = (input)=>{
    return (input === null || input === void 0 ? void 0 : input.entryType) === "navigation";
};
const useTrackNextRouterEvents = (trackEvent, pathsWithCustomAdobePageViewEventImplementations)=>{
    const router = useRouter();
    const handleRouteChange = ()=>{
        let prevLocation = JSON.parse(sessionStorage.getItem("prevLocation") ?? "{}");
        if ((prevLocation === null || prevLocation === void 0 ? void 0 : prevLocation.pathname) === undefined) {
            prevLocation = undefined;
        }
        // eslint-disable-next-line no-restricted-globals
        updatePageInfo({
            location,
            prevLocation
        });
        updateVehicleInformation({});
        const eventName = prevLocation ? DirectCallRules.NAME_PAGE_VIEW : DirectCallRules.NAME_INITIAL_PAGE_LOAD;
        const isPageView = eventName === DirectCallRules.NAME_PAGE_VIEW;
        if (isPageView) {
            resetProductDisplay();
        }
        if (isBrowser) {
            var ref, ref1, ref2;
            const eventType = (pathsWithCustomAdobePageViewEventImplementations === null || pathsWithCustomAdobePageViewEventImplementations === void 0 ? void 0 : pathsWithCustomAdobePageViewEventImplementations.includes(window.location.pathname)) ? TrackingEventTypes.PAGE_NO_ADOBE : TrackingEventTypes.PAGE;
            trackEvent({
                type: eventType,
                eventName: (ref = window.adobeData) === null || ref === void 0 ? void 0 : (ref1 = ref.page) === null || ref1 === void 0 ? void 0 : (ref2 = ref1.pageInfo) === null || ref2 === void 0 ? void 0 : ref2.pageName,
                data: getStandardPageInfo(window.location.pathname, prevLocation === null || prevLocation === void 0 ? void 0 : prevLocation.pathname)
            });
            const params = new URLSearchParams(window.location.search);
            const ttclid = params.get(TIKTOK_CLICKID_QUERY_PARAMETER);
            if (ttclid) {
                trackEvent({
                    type: TrackingEventTypes.IDENTIFY,
                    data: {
                        ttclid
                    }
                });
            }
        }
    };
    // eslint-disable-next-line unicorn/consistent-function-scoping
    const onRouteChangeStart = ()=>{
        if (isBrowser) {
            sessionStorage.setItem("prevLocation", JSON.stringify(window.location));
        }
    };
    const onRouteChangeComplete = (_url, { shallow  })=>{
        if (shallow) return;
        handleRouteChange();
    };
    const onCachedPageShow = (event)=>{
        const navigation = performance.getEntriesByType("navigation")[0];
        if (isPerformanceNavigationTiming(navigation)) {
            const navigationType = navigation.type;
            if (event.persisted && navigationType === "back_forward") {
                handleRouteChange();
            }
        }
    };
    useEffect(()=>{
        if (!router) {
            return;
        }
        handleRouteChange();
        router.events.on("routeChangeStart", onRouteChangeStart);
        router.events.on("routeChangeComplete", onRouteChangeComplete);
        window.addEventListener("pageshow", onCachedPageShow);
        return ()=>{
            router.events.off("routeChangeStart", onRouteChangeStart);
            router.events.off("routeChangeComplete", onRouteChangeComplete);
            window.removeEventListener("pageshow", onCachedPageShow);
        };
    }, []);
};
const useDatadog = ()=>{
    const [hasInitialised, setHasInitialised] = useState(false);
    const getSampleRate = ()=>{
        const fromEnv = Number.parseInt(readFromEnv(Env.DataDogRumGlobalSampleRate), 10);
        return fromEnv && !Number.isNaN(fromEnv) ? fromEnv : 50;
    };
    const getReplaySampleRate = ()=>{
        const fromEnv = Number.parseInt(readFromEnv(Env.DataDogRumGlobalReplaySampleRate), 10);
        return fromEnv && !Number.isNaN(fromEnv) ? fromEnv : 50;
    };
    const datadogEnvironment = {
        appVersion: readFromEnv(Env.BuildId),
        applicationId: readFromEnv(Env.DataDogRumApplicationId),
        dataDogClientId: readFromEnv(Env.DataDogClientId),
        datadogRumClientToken: readFromEnv(Env.DataDogRumClientToken),
        rumGlobalSampleRate: getSampleRate(),
        rumGlobalSessionReplayRate: getReplaySampleRate(),
        serviceName: readFromEnv(Env.DataDogServiceName)
    };
    useEffect(()=>{
        if (!hasInitialised) {
            setHasInitialised(true);
            initDataDogLogging(datadogEnvironment);
            initDataDogRUM(datadogEnvironment);
        }
    }, [
        hasInitialised
    ]);
};
const featureFlags = {
    enabledZendeskScript: readFromEnv(Env.EnabledZendeskScript) === "true"
};
export const AnalyticsManager = ({ pathsWithCustomAdobePageViewEventImplementations , enableLiveChat =true  })=>{
    var ref;
    const { status: userStatus , token  } = useUserStore();
    const { eventsQueue , processEventsQueue , hasAcceptedCookies , hasLoadedScripts , trackEvent  } = useAnalytics();
    const IS_MOBILE_APP = "object" !== "undefined" && ((ref = window.navigator) === null || ref === void 0 ? void 0 : ref.userAgent.includes("cinchapp"));
    useDatadog();
    useTrackNextRouterEvents(trackEvent, pathsWithCustomAdobePageViewEventImplementations);
    useEffect(()=>{
        processEventsQueue();
    }, [
        eventsQueue,
        processEventsQueue,
        hasAcceptedCookies,
        hasLoadedScripts
    ]);
    useEffect(()=>{
        if (userStatus === "valid") {
            trackEvent({
                type: TrackingEventTypes.IDENTIFY,
                userId: token === null || token === void 0 ? void 0 : token.profile["https://cinch.co.uk/user_id"]
            });
        }
    }, [
        token === null || token === void 0 ? void 0 : token.profile.sub,
        trackEvent,
        userStatus
    ]);
    const analyticsScripts = `https://assets.adobedtm.com/${readFromEnv(Env.Adobe)}.js`;
    const marketingScripts = [
        `https://www.googletagmanager.com/gtag/js?id=DC-10250799`,
        `https://www.googletagmanager.com/gtag/js?id=G-P06Z9MC4S`,
        "https://p.teads.tv/teads-fellow.js"
    ];
    const cookiePreferences = getCookieConsentPreferences("cp");
    return /*#__PURE__*/ _jsxs(React.Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Script, {
                id: "segment-snippet",
                dangerouslySetInnerHTML: {
                    __html: segmentSnippet
                },
                strategy: "beforeInteractive"
            }, "segment-snippet"),
            /*#__PURE__*/ _jsx(Script, {
                "data-testid": "data-layer",
                id: "data-layer",
                strategy: "beforeInteractive",
                dangerouslySetInnerHTML: {
                    __html: `window.adobeData = ${JSON.stringify({
                        page: {
                            pageInfo: {
                                brand: "cinch",
                                pageName: "",
                                pageURL: "",
                                previousPageName: ""
                            },
                            user: {
                                aaId: ""
                            }
                        }
                    })};`
                }
            }, "data-layer"),
            /*#__PURE__*/ _jsx(Script, {
                id: "digital-data-layer",
                strategy: "beforeInteractive",
                dangerouslySetInnerHTML: {
                    __html: `
            window.digitalData = window.digitalData || [];
            window.dataLayer = window.dataLayer || [];
            window.gtag = function () { window.dataLayer.push(arguments) }
          `
                }
            }, "digital-data-layer"),
            /*#__PURE__*/ _jsx(SimpleAnalyticsScripts, {}),
            /*#__PURE__*/ _jsx(Script, {
                id: "AWIN",
                src: "https://www.dwin1.com/30879.js",
                type: "text/javascript"
            }),
            (cookiePreferences === null || cookiePreferences === void 0 ? void 0 : cookiePreferences.analytics) && /*#__PURE__*/ _jsx(Script, {
                id: "adobe-analytics",
                src: analyticsScripts
            }, "adobe-analytics"),
            (cookiePreferences === null || cookiePreferences === void 0 ? void 0 : cookiePreferences.advertising) && marketingScripts.map((marketingScript, index)=>/*#__PURE__*/ _jsx(Script, {
                    id: `marketing-analytics-${index}`,
                    src: marketingScript
                }, `marketing-analytics-${index}`)),
            featureFlags.enabledZendeskScript && enableLiveChat && !IS_MOBILE_APP ? /*#__PURE__*/ _jsx(Script, {
                ...zendeskMessengerScriptProps({
                    id: "ze-snippet",
                    env: process.env["STAGE_NAME"] || "development"
                })
            }) : null
        ]
    }, "analytics-manager-scripts");
};
